import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';

import { setDocBuilderContext, type DocBuilderFederationContext } from '@/common/infrastructure/observability/docBuilderContext';
import { TEXT_COLORS } from '@/common/presentation/constants/colors';

export interface FederationRootErrorBoundaryProps {
  children: ReactNode;
  scope: string;
  context?: DocBuilderFederationContext;
  fallback?: ReactNode;
}

interface FederationRootErrorBoundaryState {
  hasError: boolean;
  errorMessage: string;
}

export class FederationRootErrorBoundary extends Component<FederationRootErrorBoundaryProps, FederationRootErrorBoundaryState> {
  constructor(props: FederationRootErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error: Error): FederationRootErrorBoundaryState {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidMount(): void {
    if (this.props.context) {
      setDocBuilderContext(this.props.context);
    }
  }

  componentDidUpdate(prevProps: FederationRootErrorBoundaryProps): void {
    if (this.props.context && this.props.context !== prevProps.context) {
      setDocBuilderContext(this.props.context);
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    Sentry.captureException(error, {
      tags: {
        federationScope: this.props.scope,
        section: this.props.context?.section,
        submodule: this.props.context?.submodule
      },
      contexts: {
        react: {
          componentStack: info.componentStack
        }
      }
    });
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, errorMessage: '' });
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback) return this.props.fallback;

    return (
      <Box sx={{ p: 2 }} role="alert" data-testid={`document-builder:federation-error:${this.props.scope}`}>
        <Alert severity="error" sx={{ mb: 1 }}>
          Document Builder failed to load ({this.props.scope}).
        </Alert>
        <Typography variant="body2" sx={{ color: TEXT_COLORS.MUTED, mb: 1 }}>
          {this.state.errorMessage || 'An unexpected error occurred.'}
        </Typography>
        <Button size="small" variant="outlined" onClick={this.handleRetry}>
          Retry
        </Button>
      </Box>
    );
  }
}
